import { createSlice } from '@reduxjs/toolkit';
import * as aiqConfig from 'config';
import { WS_CONNECTION_STATE } from 'config';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { STREAM_TYPE } from 'services/CameraStream/config';
import { CAMERA } from 'utils/constants';
import { CLIENT_MODE, resolveClientMode } from 'utils/readIsClientModeEnabled';
import { transformCameraConfiguration } from 'utils/utils';
import { withUsableSubStreams } from 'utils/transactionSubStream';

const {
    DESKS_TAB: { ID: desksTabId },
    REOPENED_TRANSACTION_TAB: { ID: reopenedTransactionTabId },
    VIDEO_WALL_TAB: { ID: videoWallTabId },
    TRANSACTIONS_SPLIT_VIEW_TAB: { ID: transactionsSplitViewTabId },
} = aiqConfig.TRANSACTION_PAGE_TABS;

export function getGroupKey(txn) {
    return `${txn.locationId || txn.locationName}-${txn.gateDirection}-${txn.gateId || txn.gateName}`;
}

// Default Video Wall visibility: only in-saturation Primary cameras are shown; every other camera
// (auxiliary, or not in saturation) starts hidden. Shared by initial load seeding and "Default" reset.
export function getDefaultCameraHidden(camera) {
    return !(camera.inSaturation && !camera.auxiliaryCamera);
}

export function resolveTransactionId(message) {
    return message?.truck_transaction_id > 0 ? message.truck_transaction_id : message?.gate_transaction_id;
}

// Pure helper: builds the agentReview object stored on a transaction from a raw
// .AgentReviewDeskMessage payload. Kept here so both the "transaction exists" and the
// "buffered / applied on creation" paths stay in sync.
export function buildAgentReview(payload) {
    const { outcome, reason, source, desk_id: deskId, timestamp } = payload || {};
    const reviewTransaction = payload?.transaction || {};
    const protocolDescription =
        reviewTransaction.protocol_description ||
        reviewTransaction.authorization_description ||
        reviewTransaction.safety?.description ||
        reviewTransaction.person?.authorization_description ||
        null;

    return {
        outcome,
        reason,
        source,
        deskId,
        timestamp,
        valid: !!reviewTransaction.valid,
        protocolDescription,
    };
}

const initialState = {
    //checking AIQ Server Health
    stompBrokerConnectionState: WS_CONNECTION_STATE.CONNECTING,

    splitViewMode: false,
    splitViewActiveTransactionId: null,
    splitViewActiveTransactionGroup: null,
    laneArrivalOrder: [],

    //transactions
    transactions: [],
    // Agent reviews (.AgentReviewDeskMessage) that arrived before their transaction was
    // assigned to the desk. Keyed by transactionId, applied on handleNewTransaction.
    pendingAgentReviews: {},
    hasReopenedTransaction: false,

    lastClosedTransactionId: 0,
    updatingTransactionInProgress: {},
    reopenTransaction: {},

    //enabling/disabling transaction/videoWallMode on transactionpage
    changingOperationalModeInProgress: false,
    isReceivingTransactionsEnabled: resolveClientMode(CLIENT_MODE.RECEIVING_TRANSACTIONS),
    isVideoWallEnabled: resolveClientMode(CLIENT_MODE.VIDEO_WALL),
    //video wall
    videoWallCameras: {},
    fetchingVideoWallCamerasInProgress: true,
    videoWallTransactionNotifications: {},
    videoWallFinishedTransactionIds: {},
    videoWallAlisaTransactionDetection: [],
    transactionDetectionAgentInformation: {},
    compactVideoWallMode: true,
    collapsedCompactSortedType: 'urgency',
    collapsedCompactSortDirections: {
        urgency: false,
        location: true,
        direction: true,
        detectionOf: true,
        intercom: true,
    },
    showCollapsedSortInfo: false,
    videoWallCamerasRealTimeUpdated: [],
    // Ids of video wall cameras shown in the collapsed sidebar (compact mode) instead of the main grid.
    videoWallCollapsedCameraIds: [],
    videoWalls: [],
    // Circle Check is a per-session mode toggled from the video wall header; defaults to on.
    circleCheckActive: true,

    //presentational
    newTransactionTabId: 1,
    activeTab: 0,
    visitedTransactionTabIds: {},

    //videoStreams
    requestFullScreenForTabId: 0,
    isLoadingRealTimeStreams: false,
    showRealTimeUpdateMessages: false,
    showNewCameras: false,
    cameraStreamWindow: { isOpen: false, camInfo: {} },

    //audio
    tabsAudio: {},
    activeTalkUrl: '',
    activeDemoTalkUrl: '',
    talkSessionPaused: false,
    talkSessionStarting: false,

    //sharedWorker
    sharedWorker: null,

    //report issues
    reportIssuesModalState: false,
    selectedCameraIssues: [],
    selectedIssueUrl: null,
    issueTitle: null,
    issueDataLoggs: null,
    isImageDigitalZoomed: false,

    sheetAnnouncementsByTransactionId: {},
    sheetAnnouncementPendingGate: {},

    //pinned lanes
    pinnedLanes: [],
    maxPinnedLanes: 2,
    laneActionInProgress: {},
    videoWallPinnedLanes: {},

    //editTransaction
    editTransactionModal: false,
    editTransactionModalDelete: false,
};

const transactionsSlice = createSlice({
    name: 'transactions',
    initialState,
    reducers: {
        setStompBrokerConnectionState(state, { payload }) {
            state.stompBrokerConnectionState = payload;
        },

        unsubscribeFromStompBrokerTopic() {},
        subscribeToStompBrokerTopic() {},

        disconnectStompBroker(state) {
            state.isStompBrokerConnected = false;
            state.isStompBrokerConnectionInProgress = false;
        },

        setActiveTab(state, { payload }) {
            state.activeTab = payload;
            state.visitedTransactionTabIds ??= {};
            if (state.transactions.some(({ tabId }) => tabId === payload)) state.visitedTransactionTabIds[payload] = true;
        },

        markTransactionTabVisited(state, { payload: tabId }) {
            state.visitedTransactionTabIds ??= {};
            state.visitedTransactionTabIds[tabId] = true;
        },

        removeTransaction(state, { payload: transactionId }) {
            let transactions = state.transactions;
            const removedTransaction = transactions.find((transaction) => transaction.id === transactionId);
            state.transactions = transactions.filter((transaction) => transaction.id !== transactionId);
            state.visitedTransactionTabIds ??= {};
            if (removedTransaction) delete state.visitedTransactionTabIds[removedTransaction.tabId];

            // Clean up sheet-announcement state tied to the removed transaction.
            delete state.sheetAnnouncementsByTransactionId[transactionId];
            if (state.pendingAgentReviews) delete state.pendingAgentReviews[transactionId];

            Object.keys(state.sheetAnnouncementPendingGate).forEach((gateId) => {
                if (state.sheetAnnouncementPendingGate[gateId] === transactionId) {
                    delete state.sheetAnnouncementPendingGate[gateId];
                }
            });

            const activeKeys = new Set(state.transactions.map(getGroupKey));
            state.laneArrivalOrder = state.laneArrivalOrder.filter((key) => activeKeys.has(key));

            state.activeTab = state.transactions.length ? state.transactions[0].tabId : desksTabId;
        },

        setTransactions(state, { payload: newTransactions }) {
            state.transactions = newTransactions;
            state.visitedTransactionTabIds ??= {};

            const activeTabIds = new Set(newTransactions.map(({ tabId }) => String(tabId)));
            Object.keys(state.visitedTransactionTabIds).forEach((tabId) => {
                if (!activeTabIds.has(tabId)) delete state.visitedTransactionTabIds[tabId];
            });

            const activeKeys = new Set(newTransactions.map(getGroupKey));
            state.laneArrivalOrder = state.laneArrivalOrder.filter((key) => activeKeys.has(key));
        },

        toggleIsReceivingTransactionsEnabled() {},
        setIsReceivingTransactionsEnabled(state, { payload }) {
            state.isReceivingTransactionsEnabled = payload;
        },

        setIsVideoWallEnabled(state, { payload }) {
            state.isVideoWallEnabled = payload;
        },

        setCircleCheckActive(state, { payload }) {
            state.circleCheckActive = payload;
        },

        setCollapsedCompactSortedType(state, { payload }) {
            state.collapsedCompactSortedType = payload;
        },

        resetCollapsedCompactSort(state) {
            state.collapsedCompactSortedType = 'none';
            state.collapsedCompactSortDirections = {
                urgency: true,
                location: true,
                direction: true,
                detectionOf: true,
                intercom: true,
            };
        },

        setShowCollapsedSortInfo(state, { payload }) {
            state.showCollapsedSortInfo = payload;
        },

        setCollapsedCompactSortDirection(state, { payload: { type, direction } }) {
            state.collapsedCompactSortDirections[type] = direction;
        },

        setFetchingVideoWallCamerasInProgress(state, { payload: progress }) {
            state.fetchingVideoWallCamerasInProgress = progress;
        },
        removeVideoWallCameras(state) {
            state.videoWallCameras = {};
        },
        setVideoWallCameras(state, { payload: cameras }) {
            const transformedCameras = transformCameraConfigArray(cameras);

            const videoWallCameras = { level1: [], talkUrls: [] };

            const tabAudio = {
                isTabMuted: false,
                soundOnStreamId: '',
                availableAudioStreams: [],
            };

            let counter = 1;
            for (let cameraId of Object.keys(transformedCameras)) {
                let gateCameraId = [transformedCameras[cameraId][0].id];
                for (let camConfig of transformedCameras[cameraId]) {
                    if (!gateCameraId.includes(camConfig.id)) {
                        ++counter;
                        gateCameraId.push(camConfig.id);
                    }
                    const camera = getCameraConfig(camConfig, `[L1]${counter}`, videoWallTabId, videoWallCameras, tabAudio);
                    camera.hidden = getDefaultCameraHidden(camera);
                    videoWallCameras.level1.push(camera);
                }

                ++counter;
            }

            if (!tabAudio.soundOnStreamId) tabAudio.isTabMuted = true;

            state.tabsAudio[videoWallTabId] = tabAudio;
            state.videoWallCameras = videoWallCameras;
            state.activeTab === desksTabId && (state.activeTab = videoWallTabId);
        },
        setRealTimeVideoWallCameras(state, { payload }) {
            console.log('setRealTimeVideoWallCameras(): real-time update video wall after changes in saturation', payload);
            if (!Array.isArray(payload)) {
                state.isLoadingRealTimeStreams = false;
                state.showRealTimeUpdateMessages = false;
                return;
            }

            const videoWallCameras = state.videoWallCameras || { level1: [], talkUrls: [] };
            const tabAudio = state.tabsAudio[videoWallTabId] || {
                isTabMuted: false,
                soundOnStreamId: '',
                availableAudioStreams: [],
            };

            let added = false;
            payload.forEach((item) => {
                const cameraInfo = item.gate_camera_info;
                if (item.gate_update_type === 'ADDED') {
                    state.videoWallCamerasRealTimeUpdated = [...state.videoWallCamerasRealTimeUpdated, cameraInfo.id];
                    const exists = videoWallCameras.level1.some((cam) => cam.camId === cameraInfo.camId);
                    if (!exists) {
                        const camera = getCameraConfig(cameraInfo, `[L1]${cameraInfo.id}`, videoWallTabId, videoWallCameras, tabAudio);
                        camera.hidden = getDefaultCameraHidden(camera);
                        videoWallCameras.level1.push(camera);
                        added = true;
                    }
                } else if (item.gate_update_type === 'REMOVED') {
                    state.videoWallCamerasRealTimeUpdated = state.videoWallCamerasRealTimeUpdated.filter((id) => id !== cameraInfo.id);
                    const camera = videoWallCameras.level1.find((cam) => cam.gateCameraId === cameraInfo.id);
                    if (camera) {
                        cameraStreamManager.removeVideoOutput(cameraStreamManager.getVideoElementId(videoWallTabId, camera.id), false);
                        // Drop the removed camera from the collapsed set too. The compact sidebar is reconciled
                        // from videoWallCollapsedCameraIds (not from level1), so without this a collapsed camera
                        // from a de-saturated location would linger in the sidebar even though its stream and
                        // its main-grid tile are gone.
                        state.videoWallCollapsedCameraIds = (state.videoWallCollapsedCameraIds || []).filter((id) => id !== camera.id);
                    }
                    videoWallCameras.level1 = videoWallCameras.level1.filter((cam) => cam.gateCameraId !== cameraInfo.id);
                    if (cameraInfo.talk_url) {
                        videoWallCameras.talkUrls = videoWallCameras.talkUrls.filter((url) => url !== cameraInfo.talk_url);
                    }
                }
            });

            state.videoWallCameras = videoWallCameras;
            state.tabsAudio[videoWallTabId] = tabAudio;
            state.isLoadingRealTimeStreams = false;
            state.showNewCameras = added;
        },
        resetVideoWallCamerasRealTimeUpdated(state) {
            state.videoWallCamerasRealTimeUpdated = [];
        },
        setCheckedStateToVideoCameras(state, { payload }) {
            const hiddenCamerasIds = payload.reduce((arr, camera) => {
                if (!camera.hidden) arr.push(camera.gateCameraId);
                return arr;
            }, []);

            state.videoWallCameras.level1 = state.videoWallCameras.level1.map((camConfig) => {
                camConfig.hidden = !hiddenCamerasIds.includes(camConfig.gateCameraId);
                return camConfig;
            });
        },
        setVideoWallCollapsedCameraIds(state, { payload }) {
            state.videoWallCollapsedCameraIds = Array.isArray(payload) ? [...new Set(payload)] : [];
        },
        setVideoWalls(state, { payload }) {
            state.videoWalls = Array.isArray(payload) ? payload : [];
        },
        // saga-only action (handled in transactionsSagas onSaveMultiWallConfig)
        saveMultiWallConfig() {},
        // saga-only action (handled in transactionsSagas onQueueNewVideoWallConfig)
        queueNewVideoWallConfig() {},
        resetVideoWallToDefault(state) {
            if (state.videoWallCameras?.level1) {
                state.videoWallCameras.level1 = state.videoWallCameras.level1.map((camConfig) => {
                    camConfig.hidden = getDefaultCameraHidden(camConfig);
                    return camConfig;
                });
            }
            state.videoWallCollapsedCameraIds = [];
        },
        getVideoWallCameras() {},
        toggleVideoWallEnabled() {},

        setCameraStreamWindow(state, { payload: { isOpen, camInfo } }) {
            state.cameraStreamWindow = { isOpen, camInfo };
        },

        setChangingOperationalModeInProgress(state, { payload }) {
            state.changingOperationalModeInProgress = payload;
        },

        setUpdatingTransactionInProgress(state, { payload: { isUpdateInProgress, transactionId, initialState } }) {
            if (initialState) {
                state.updatingTransactionInProgress = {};
                return;
            }
            if (isUpdateInProgress) {
                state.updatingTransactionInProgress[transactionId] = true;
            } else {
                /* eslint-disable-next-line no-unused-vars */
                const { [transactionId]: toBeDeleted, ...rest } = state.updatingTransactionInProgress;
                state.updatingTransactionInProgress = rest;
            }
        },

        setLastClosedTransactionId(state, { payload: closedTransactionId = 0 }) {
            state.lastClosedTransactionId = closedTransactionId;
        },

        setHasReopenedTransaction(state, { payload: hasReopenedTransaction = false }) {
            state.hasReopenedTransaction = hasReopenedTransaction;
        },

        setReopenTransaction(state, { payload }) {
            state.reopenTransaction = payload;
        },

        handleNewTransaction(state, { payload: incomingTransaction }) {
            const allTransactions = state.transactions;
            const isReopened = incomingTransaction.reopened;
            const tabId = isReopened ? reopenedTransactionTabId : state.newTransactionTabId++;
            const newTransaction = {
                tabId: tabId,
                uuid: incomingTransaction.uuid,
                timestamp: {
                    received: incomingTransaction.timestamp,
                },
                locationId: incomingTransaction.location_id,
                locationName: incomingTransaction.location_name,
                gateId: incomingTransaction.gate_id,
                gateName: incomingTransaction.gate_name,
                gateDirection: incomingTransaction.gate_direction,
                deskId: incomingTransaction.desk_id,
                id: resolveTransactionId(incomingTransaction),
                version: incomingTransaction.version,
                reopened: incomingTransaction.reopened,
                camsConfig: { level1: [], level2: [], talkUrls: [] },
                companyId: incomingTransaction.company_id,
                source: incomingTransaction.source,
            };

            const tabAudio = {
                isTabMuted: false,
                soundOnStreamId: '',
                availableAudioStreams: [],
            };
            newTransaction.camsConfig.level1 = toLevelCameraConfigs(
                incomingTransaction.cameras.level1,
                'L1',
                tabId,
                newTransaction.camsConfig,
                tabAudio
            );
            newTransaction.camsConfig.level2 = toLevelCameraConfigs(
                incomingTransaction.cameras.level2,
                'L2',
                tabId,
                newTransaction.camsConfig,
                tabAudio
            );

            if (!tabAudio.soundOnStreamId) tabAudio.isTabMuted = true;

            // Apply an agent review that arrived before this transaction was created (buffered
            // by agentReviewMessageReceived), so the TransactionStepBanner renders immediately.
            const pendingAgentReview = state.pendingAgentReviews?.[newTransaction.id];
            if (pendingAgentReview) {
                newTransaction.agentReview = pendingAgentReview;
                newTransaction.agentReviewRev = (newTransaction.agentReviewRev || 0) + 1;
                delete state.pendingAgentReviews[newTransaction.id];
            }

            state.tabsAudio[tabId] = tabAudio;
            const groupKey = getGroupKey(newTransaction);

            if (!state.laneArrivalOrder.includes(groupKey)) {
                state.laneArrivalOrder.push(groupKey);
            }

            !isReopened && state.transactions.push(newTransaction);
            if (isReopened) {
                state.transactions.unshift(newTransaction);
                state.hasReopenedTransaction = true;
            }

            state.activeTab =
                allTransactions.length === 1 || isReopened
                    ? state.splitViewMode
                        ? transactionsSplitViewTabId
                        : newTransaction.tabId
                    : state.activeTab;
        },

        updateTransactionVersion(state, { payload: { transactionId, version } }) {
            state.transactions.forEach((transaction) => {
                if (transaction.id === transactionId) transaction.version = version;
            });
        },

        closeTransactionPanel(state, { payload: { closeAllPanels = false, tabId } }) {
            if (closeAllPanels) {
                state.newTransactionTabId = 1;
                state.videosFailedToLoad = {};
                state.videosFreezeDetected = {};
                state.tabsAudio = {};
                state.lastClosedTransactionId = 0;
                state.videoWallAlisaTransactionDetection = [];
                state.transactionDetectionAgentInformation = {};
                state.laneArrivalOrder = [];
                state.visitedTransactionTabIds = {};
                return;
            }
            cleanTransactionPanelOnClose(state, tabId);
        },

        cleanTransactionPanelOnClose(state, { payload: tabId }) {
            cleanTransactionPanelOnClose(state, tabId);
        },

        setRequestFullScreenForTabId(state, { payload: tabId }) {
            state.requestFullScreenForTabId = tabId;
        },

        setTabAudioMuted(state, { payload: { tabId, muted } }) {
            state.tabsAudio[tabId] && (state.tabsAudio[tabId].isTabMuted = muted);
        },

        setTabAudioSoundOnStreamId(state, { payload: { tabId, soundOnStreamId, muteTab } }) {
            if (muteTab) {
                state.tabsAudio[tabId].isTabMuted = true;
                return;
            }
            const currentSoundOnStreamId = state.tabsAudio[tabId].soundOnStreamId;
            const isTabMuted = state.tabsAudio[tabId].isTabMuted;

            if (currentSoundOnStreamId !== soundOnStreamId) {
                state.tabsAudio[tabId].soundOnStreamId = soundOnStreamId;
                state.tabsAudio[tabId].isTabMuted = false;
            } else state.tabsAudio[tabId].isTabMuted = !isTabMuted;
        },

        setActiveTalkUrl(state, { payload: { activeTalkUrl, sendSignal = true } }) {
            state.activeTalkUrl = activeTalkUrl;
            if (!activeTalkUrl && state.talkSessionPaused) state.talkSessionPaused = false;
            state.talkSessionStarting = !!activeTalkUrl && !!sendSignal;
        },

        setActiveDemoTalkUrl(state, { payload: { talkUrl } }) {
            state.activeDemoTalkUrl = talkUrl;
        },

        setTalkSessionStarting(state, { payload }) {
            state.talkSessionStarting = payload;
        },
        setTalkSessionPaused(state, { payload }) {
            state.talkSessionPaused = payload;
        },

        deskAssignedMessageReceived() {},

        // transactionSubStreamConfigEnabled: these cameras' SUB entries became unusable (see withUsableSubStreams), so
        // their transaction tiles stop picking SUB
        dropCameraSubStreams(state, { payload: cameraIds }) {
            const dropped = new Set(cameraIds);
            state.transactions.forEach(({ camsConfig }) => {
                ['level1', 'level2'].forEach((level) => {
                    camsConfig[level] = camsConfig[level].filter(
                        (camera) => !(dropped.has(camera.camId) && camera.videoData.streamType === STREAM_TYPE.SUB)
                    );
                    camsConfig[level].forEach((camera) => dropped.has(camera.camId) && delete camera.hasSubStream);
                });
            });
        },

        // TransactionImageStatusMessage: a Macaw image of a transaction changed status (queued, uploaded to S3, failed)
        transactionImageStatusReceived() {},

        // reloads the Macaw image links of a transaction, e.g. when a presigned link expired
        refreshTransactionImages() {},

        agentReviewMessageReceived(state, { payload }) {
            const transactionId = resolveTransactionId(payload);
            const { outcome, reason } = payload || {};
            const agentReview = buildAgentReview(payload);
            const transaction = state.transactions.find((t) => t.id === transactionId);

            if (!transaction) {
                // The .AgentReviewDeskMessage can arrive before the transaction is assigned to
                // the desk (race between DeskAssignedMessage and AgentReviewDeskMessage). Buffer
                // it keyed by transactionId so handleNewTransaction can apply it on creation,
                // instead of dropping it and never rendering the banner.
                console.warn(
                    `agentReviewMessageReceived(): no matching transaction for transactionId=${transactionId}, outcome=${outcome}, reason=${reason}. Buffering agent review; banner will render once the transaction is assigned to the desk.`
                );
                if (!state.pendingAgentReviews) state.pendingAgentReviews = {};
                state.pendingAgentReviews[transactionId] = agentReview;
                return;
            }

            transaction.agentReview = agentReview;
            transaction.agentReviewRev = (transaction.agentReviewRev || 0) + 1;
            if (state.pendingAgentReviews) delete state.pendingAgentReviews[transactionId];
        },

        agentReviewTellDriver() {},

        setCameraConfig(state, { payload: { transactionIdsArr, camConfig, activeOnVideoWall } }) {
            state.transactions.forEach((transaction) => {
                if (transactionIdsArr.includes(transaction.id)) {
                    const transactionCams = transaction.camsConfig;
                    [transactionCams.level1, transactionCams.level2].forEach((level) => replaceCameraStreamConfig(level, camConfig));
                }
            });
            if (activeOnVideoWall) {
                replaceCameraStreamConfig(state.videoWallCameras.level1, camConfig);
            }
        },

        invalidateCameraConfigByKurentoId(state, { payload: kurentoId }) {
            const invalidateCamConfig = (camConfig) => {
                if (camConfig.kurentoId === kurentoId) {
                    camConfig.invalidated = true;
                }
            };

            state.transactions.forEach((transaction) => {
                const transactionCams = transaction.camsConfig;
                [...transactionCams.level1, ...transactionCams.level2].forEach(invalidateCamConfig);
            });

            state.videoWallCameras.level1?.forEach(invalidateCamConfig);
        },

        removeVideoWallTransactionNotification(state, { payload }) {
            const videoWallTransactionNotifications = state.videoWallTransactionNotifications;
            // Notifications are keyed by lane (gateId) so every camera of a lane - primary and auxiliary -
            // shares one notification. No gateCameraId fallback: the two are independent id sequences, so
            // falling back would let one lane's message clear another's notification (and the UI resolves
            // notifications by gateId only, so a gateCameraId-keyed entry would render nowhere anyway).
            const gateId = payload?.gateId;
            const truckTransactionId = payload?.truckTransactionId;
            const forceNotificationRemoval = payload?.forceNotificationRemove;
            const expectedFinishedAt = payload?.expectedFinishedAt;

            if (gateId == null) return;
            const notification = videoWallTransactionNotifications[gateId];

            if (!notification) return;
            if (forceNotificationRemoval && expectedFinishedAt !== undefined && notification.finishedAt !== expectedFinishedAt) return;

            const matchesTransactionId = (transaction) => String(transaction.truckTransactionId) === String(truckTransactionId);

            let removeCameraNotification =
                forceNotificationRemoval ||
                notification.transactions.length === 0 ||
                (notification.transactions.length === 1 && matchesTransactionId(notification.transactions[0]));

            if (removeCameraNotification) {
                // eslint-disable-next-line
                const { [gateId]: toBeDeleted, ...newVWNoitifications } = videoWallTransactionNotifications;
                state.videoWallTransactionNotifications = newVWNoitifications;
            } else {
                notification.transactions = notification.transactions.filter((transaction) => !matchesTransactionId(transaction));
            }
        },

        // Assignment write for a lane. The invariant is one entry per truckTransactionId, with the assignee
        // last-write-wins: a transaction can be assigned more than once (return to queue reassigns it to
        // another desk, and that fan-out reaches the wall twice - once directly, once through the
        // aiq.desk.assigned round trip). Appending instead of upserting left the OLD assignee sitting at
        // transactions[0], which is the entry every consumer renders and sorts by. Entries for DIFFERENT
        // transactions still accumulate, so a lane processing a double trailer keeps one entry per transaction.
        updateVideoWallTransactionNotification(state, { payload }) {
            const videoWallTransactionNotifications = state.videoWallTransactionNotifications;
            const gateId = payload?.gateId;
            const assignee = payload?.accountUsername;
            const truckTransactionId = payload?.truckTransactionId;
            if (gateId == null) return;
            // Entries are matched by truckTransactionId everywhere (here, in removeVideoWallTransactionNotification
            // and in handleTransactionStatusVideoWallMessage), so an entry without one could never be cleared by a
            // finish or a discard. Drop such a message instead of locking the lane with it.
            if (truckTransactionId == null) return;

            // No notification means no detection message reached this client - a wall opened mid-transaction, or a
            // lane whose notification already hit its timeout. The assignment itself proves the lane is busy, so
            // build the entry from it rather than dropping the message and rendering the lane as free (which also
            // invites another agent to try to take it). The saga arms the force-removal timer for this case.
            if (!videoWallTransactionNotifications[gateId]) {
                videoWallTransactionNotifications[gateId] = {
                    gateId,
                    gateCameraId: payload?.gateCameraId,
                    // A rebuilt lane has no detection behind it, so this is when the assignment arrived, not
                    // when the truck did. Consumers read timestamp as arrival time, so the duration chip
                    // restarts from zero and sortByUrgency ranks the lane as least urgent even if the truck
                    // has been waiting. It is the only timestamp available here, and still better than
                    // rendering the lane as free. A real fix needs the server to seed lane assignments.
                    timestamp: payload?.timestamp ?? Date.now(),
                    transactions: [],
                };
            }

            const notification = videoWallTransactionNotifications[gateId];
            const existing = notification.transactions.find((transaction) => String(transaction.truckTransactionId) === String(truckTransactionId));

            // A fading lane plus an assignment for a transaction it already holds means this message is late:
            // the transaction it names is the one that just finished. `resendAlreadyStarted` republishes the
            // desk assignment on every repeat detection of an already-started transaction, and that travels
            // the slower aiq.desk.assigned round trip, so it can land after the FINISHED status. Acting on it
            // would un-fade a submitted transaction and hold the lane for the full notification timeout.
            if (notification.finishedAt && existing) return;

            // A new transaction on a fading lane. A lane only fades once nothing on it is still open, so every
            // entry it holds is finished: keeping them would leave a finished transaction in transactions[0],
            // the entry the tile renders, and nothing would ever clear it again (BEAST-13740).
            if (notification.finishedAt) {
                notification.transactions = [];
                notification.timestamp = payload?.timestamp ?? Date.now();
                delete notification.finishedAt;
            }

            if (existing) existing.assignee = assignee;
            else notification.transactions.push({ truckTransactionId, assignee });
        },

        completeVideoWallTransactionNotification(state, { payload }) {
            const videoWallTransactionNotifications = state.videoWallTransactionNotifications;
            const gateId = payload?.gateId;
            const finishedAt = payload?.finishedAt ?? Date.now();
            if (gateId == null) return;
            const notification = videoWallTransactionNotifications[gateId];

            if (!notification) return;

            notification.finishedAt = finishedAt;
        },

        // A lane's force-removal timer ran out; the saga decides whether the lane really goes (BEAST-13807)
        videoWallNotificationTimedOut() {},

        // Drops the lane's transactions the backend reported as ended and keeps the lane. Left with none, the lane is
        // the truck whose detection came in while the backend was asked: Unassigned from receivedAt.
        dropVideoWallLaneTransactions(state, { payload: { gateId, truckTransactionIds, receivedAt } }) {
            const notification = state.videoWallTransactionNotifications[gateId];
            if (!notification) return;

            const ended = new Set(truckTransactionIds.map(String));
            notification.transactions = notification.transactions.filter(({ truckTransactionId }) => !ended.has(String(truckTransactionId)));
            if (!notification.transactions.length) {
                notification.timestamp = receivedAt;
                notification.receivedAt = receivedAt;
            }
        },

        // Transactions this wall saw end, kept for expectedFinishedTransactionMillis like the desk side's
        // finishedTransactionIdsQueue: an assignment for one of them is a late resend (the desk assignment round trip
        // can land after the finish) and would put a finished transaction back on its lane.
        rememberFinishedVideoWallTransaction(state, { payload: truckTransactionId }) {
            state.videoWallFinishedTransactionIds[truckTransactionId] = true;
        },
        forgetFinishedVideoWallTransaction(state, { payload: truckTransactionId }) {
            delete state.videoWallFinishedTransactionIds[truckTransactionId];
        },

        addVideoWallTransactionNotification(state, { payload }) {
            const videoWallTransactionNotifications = state.videoWallTransactionNotifications;
            const gateId = payload?.gateId;
            // Without this guard a payload lacking gateId would be stored under the literal key "undefined",
            // which no consumer can ever resolve (the UI looks up by the camera's gateId).
            if (gateId == null) return;
            const currentNotification = videoWallTransactionNotifications[gateId];

            if (currentNotification && !currentNotification.finishedAt) return;
            videoWallTransactionNotifications[gateId] = { ...payload, transactions: [] };
        },

        setWindowId(state, { payload: { id: windowId, uid } }) {
            if (state.sharedWorker) {
                state.sharedWorker.windowId = windowId;
                if (uid !== undefined) state.sharedWorker.uid = uid;
            } else state.sharedWorker = { windowId, uid };
        },
        setWindowCount(state, { payload: windowCount }) {
            if (state.sharedWorker) state.sharedWorker.windowCount = windowCount;
            else state.sharedWorker = { windowCount };
        },
        setStreamingCameraTotal(state, { payload: streamingCameraTotal }) {
            if (state.sharedWorker) state.sharedWorker.streamingCameraTotal = streamingCameraTotal;
            else state.sharedWorker = { streamingCameraTotal };
        },
        registerSharedWorker() {},
        setReportIssuesModalState(state, { payload: modalState }) {
            state.reportIssuesModalState = modalState;
        },

        //report issues
        reportIssues() {},
        setSelectedCameraIssues(state, { payload: issues }) {
            state.selectedCameraIssues = issues;
        },
        setSelectedIssueUrl(state, { payload: issues }) {
            state.selectedIssueUrl = issues;
        },
        setIssueTitle(state, { payload: title }) {
            state.issueTitle = title;
        },
        setIssueDataLoggs(state, { payload }) {
            state.issueDataLoggs = payload;
        },
        showRealTimeUpdateMessages(state, { payload }) {
            const { isLoadingRealTimeStreams, showRealTimeUpdateMessages } = payload;
            state.isLoadingRealTimeStreams = isLoadingRealTimeStreams;
            state.showRealTimeUpdateMessages = showRealTimeUpdateMessages;
        },
        setImageIsDigitalZoomed(state, { payload }) {
            state.isImageDigitalZoomed = payload;
        },
        setTransactionsSplitViewModeEnabled(state, { payload: enabled }) {
            state.splitViewMode = enabled;
        },

        setSplitViewActiveTransaction(state, { payload: { transactionId = null, transactionGroup = null } = {} }) {
            state.splitViewActiveTransactionId = transactionId;
            state.splitViewActiveTransactionGroup = transactionGroup;
        },

        clearSplitViewActiveTransaction(state) {
            state.splitViewActiveTransactionId = null;
            state.splitViewActiveTransactionGroup = null;
        },

        toggleTransactionsSplitViewMode() {},

        setSheetAnnouncements(state, { payload }) {
            const { transactionId, columnsConfig, tableData, status, color } = payload;
            if (!transactionId) return;
            state.sheetAnnouncementsByTransactionId[transactionId] = {
                columnsConfig: columnsConfig || [],
                tableData: tableData || [],
                status: status || 'SUCCESS',
                color: color ?? null,
            };
        },

        setSheetAnnouncementPendingGate(state, { payload: { gateId, transactionId } }) {
            if (!gateId || !transactionId) return;
            state.sheetAnnouncementPendingGate[gateId] = transactionId;
        },

        clearSheetAnnouncements(state, { payload }) {
            const transactionId = payload?.transactionId ?? payload;
            if (transactionId) {
                delete state.sheetAnnouncementsByTransactionId[transactionId];
            } else {
                state.sheetAnnouncementsByTransactionId = {};
            }
        },
        onEditTransactionModal(state, { payload }) {
            state.editTransactionModal = payload;
        },
        onEditTransactionModalDelete(state, { payload }) {
            state.editTransactionModalDelete = payload;
        },

        pinLaneStart() {},
        unpinLaneStart() {},

        setLaneActionInProgress(state, { payload: { gateId, inProgress } }) {
            if (inProgress) {
                state.laneActionInProgress[gateId] = true;
            } else {
                delete state.laneActionInProgress[gateId];
            }
        },

        setPinnedLanes(state, { payload: gateIds }) {
            state.pinnedLanes = gateIds;
        },

        setMaxPinnedLanes(state, { payload: maxPinnedLanes }) {
            state.maxPinnedLanes = maxPinnedLanes;
        },

        // Video-wall pin overlay: all desks' pins keyed by gateId, driven by /sticky-lane/active + STOMP messages.
        setVideoWallPinnedLanes(state, { payload: lanes }) {
            state.videoWallPinnedLanes = (lanes || []).reduce((acc, { gateId, accountUsername, pinnedAt }) => {
                acc[gateId] = { accountUsername, pinnedAt };
                return acc;
            }, {});
        },

        addVideoWallPinnedLane(state, { payload: { gateId, accountUsername, pinnedAt } }) {
            state.videoWallPinnedLanes[gateId] = { accountUsername, pinnedAt };
        },

        removeVideoWallPinnedLane(state, { payload: gateId }) {
            delete state.videoWallPinnedLanes[gateId];
        },
    },
});

export const transactionsReducer = transactionsSlice.reducer;
export const transactionActions = transactionsSlice.actions;

function getCameraConfig(camera, id, tabId, cemeraConfigsObj, tabAudio) {
    if (camera.audio_type === CAMERA.AUDIO_TYPE.HIKVISION_2WA) {
        const videoOutputId = cameraStreamManager.getVideoElementId(tabId, id);
        !tabAudio.availableAudioStreams.includes(videoOutputId) &&
            tabAudio.availableAudioStreams.push(cameraStreamManager.getVideoElementId(tabId, id));
    }
    if (camera.talk_url) cemeraConfigsObj.talkUrls.push(camera.talk_url);

    ///transformCameraConfiguration is pure function and it is allowed to be used inside reducer
    return transformCameraConfiguration(camera, id);
}

function transformCameraConfigArray(cameras) {
    return cameras.reduce((transformed, camera) => {
        const generatedName = camera.generated_name;
        if (!transformed[generatedName]) transformed[generatedName] = [camera];
        else transformed[generatedName].push(camera);
        return transformed;
    }, {});
}

// One level of a desk message's cameras -> camsConfig entries, positions numbered per camera ([L1]1, [L1]2...). A camera
// can come as MAIN + SUB entries (transactionSubStreamConfigEnabled): unusable SUB entries are dropped, and the camera's
// entries are marked hasSubStream, so a tile only picks SUB when there is a SUB player to play.
function toLevelCameraConfigs(cameras, level, tabId, camsConfig, tabAudio) {
    const configs = [];
    Object.values(transformCameraConfigArray(withUsableSubStreams(cameras))).forEach((group, index) => {
        const hasSubStream = group.some((camera) => camera.stream_type === STREAM_TYPE.SUB);
        // the first entry of a camera is the one rendered: keep MAIN first whatever order the entries came in
        if (hasSubStream) group.sort((a, b) => (a.stream_type === STREAM_TYPE.SUB) - (b.stream_type === STREAM_TYPE.SUB));
        group.forEach((camera) => {
            const config = getCameraConfig(camera, `[${level}]${index + 1}`, tabId, camsConfig, tabAudio);
            configs.push(hasSubStream ? { ...config, hasSubStream } : config);
        });
    });
    return configs;
}

const replaceCameraStreamConfig = function (configArr, camConfig) {
    configArr.forEach(
        // eslint-disable-next-line
        ({ invalidated, ...camera }, i) =>
            camera.camId === camConfig.camId &&
            (camConfig.status === 'INACTIVE' ||
                (camera.videoData.streamType === camConfig.videoData.streamType &&
                    camera.videoData.playerEndpointId !== camConfig.videoData.playerEndpointId)) &&
            (configArr[i] = {
                ...camera,
                ...camConfig,
                // stream data only from an update of the same stream type. An INACTIVE update matches every entry of the
                // camera and carries no stream data (the backend sends id + status, updateCameraConfig types it 'TEST'),
                // so each entry keeps its own; overwriting it left the entry typed 'TEST', unmatchable by the next update
                videoData: camera.videoData.streamType === camConfig.videoData.streamType ? camConfig.videoData : camera.videoData,
            })
    );
};

function cleanTransactionPanelOnClose(state, tabId) {
    let videosFailedToLoad = state.videosFailedToLoad;
    let videosFreezeDetected = state.videosFreezeDetected;
    let tabsAudio = state.tabsAudio;
    /* eslint-disable no-unused-vars */
    const { [tabId]: videosToBeRemoved, ...newVideosFailedToLoad } = videosFailedToLoad;
    const { [tabId]: videostoBeRemoved, ...newVideosFreezeDetected } = videosFreezeDetected;
    const { [tabId]: toBeDeleted, ...newTabsAudio } = tabsAudio;
    /* eslint-enable no-unused-vars */
    state.videosFailedToLoad = newVideosFailedToLoad;
    state.videosFreezeDetected = newVideosFreezeDetected;
    state.tabsAudio = newTabsAudio;
    state.visitedTransactionTabIds ??= {};
    delete state.visitedTransactionTabIds[tabId];
}
